/**
 * Per-theme cinematic palette for the pre-reveal atmosphere shell.
 * Maps existing animationTheme values to a coherent atmospheric look.
 */

export interface CinematicPalette {
  /** Background gradient stops (CSS color strings, dark base) */
  bgFrom: string;
  bgVia: string;
  bgTo: string;
  /** Particle / dust tint */
  particle: string;
  /** Light ray + sweep tint */
  light: string;
  /** Vignette tint */
  vignette: string;
  /** Buildup typography color */
  textColor: string;
  /** Optional accent for the logo lockup */
  accent: string;
}

const PALETTES: Record<string, CinematicPalette> = {
  handwritten_journal: {
    bgFrom: '#0a0806', bgVia: '#1a120a', bgTo: '#0a0806',
    particle: 'rgba(212,175,120,0.55)',
    light: 'rgba(220,180,120,0.18)',
    vignette: 'rgba(0,0,0,0.65)',
    textColor: 'rgba(245,232,210,0.95)',
    accent: 'rgba(220,180,120,0.9)',
  },
  elegant_floral: {
    bgFrom: '#0c0a12', bgVia: '#1b1424', bgTo: '#0c0a12',
    particle: 'rgba(236,200,220,0.55)',
    light: 'rgba(220,170,200,0.18)',
    vignette: 'rgba(0,0,0,0.6)',
    textColor: 'rgba(245,230,238,0.95)',
    accent: 'rgba(230,180,210,0.9)',
  },
  golden_light: {
    bgFrom: '#0a0804', bgVia: '#1a1408', bgTo: '#0a0804',
    particle: 'rgba(251,191,36,0.55)',
    light: 'rgba(251,191,36,0.22)',
    vignette: 'rgba(0,0,0,0.65)',
    textColor: 'rgba(252,240,210,0.95)',
    accent: 'rgba(251,191,36,0.95)',
  },
  blooming_garden: {
    bgFrom: '#08120c', bgVia: '#101e16', bgTo: '#08120c',
    particle: 'rgba(180,230,200,0.5)',
    light: 'rgba(180,230,200,0.18)',
    vignette: 'rgba(0,0,0,0.6)',
    textColor: 'rgba(232,245,234,0.95)',
    accent: 'rgba(180,230,200,0.9)',
  },
  ornate_keepsake: {
    bgFrom: '#0a0808', bgVia: '#161010', bgTo: '#0a0808',
    particle: 'rgba(220,200,170,0.5)',
    light: 'rgba(220,200,170,0.18)',
    vignette: 'rgba(0,0,0,0.65)',
    textColor: 'rgba(240,228,210,0.95)',
    accent: 'rgba(220,200,170,0.9)',
  },
  memory_constellation: {
    bgFrom: '#040612', bgVia: '#0a0e22', bgTo: '#040612',
    particle: 'rgba(180,210,255,0.55)',
    light: 'rgba(180,210,255,0.18)',
    vignette: 'rgba(0,0,0,0.7)',
    textColor: 'rgba(225,235,255,0.95)',
    accent: 'rgba(180,210,255,0.95)',
  },
};

const DEFAULT: CinematicPalette = PALETTES.elegant_floral;

export function getCinematicPalette(themeValue?: string): CinematicPalette {
  if (!themeValue) return DEFAULT;
  return PALETTES[themeValue] ?? DEFAULT;
}
